import { useTranslation } from 'react-i18next';
import { chipPiecesModel, type ChipPiecesRow } from './chipPiecesReview';
export function MemberChipPiecesReview({
  participants,
  expectedPieces,
  countedPieces,
}: {
  participants: ChipPiecesRow[];
  expectedPieces?: Record<string, number> | null;
  countedPieces?: Record<string, number> | null;
}) {
  const { t } = useTranslation('tournament');
  const model = chipPiecesModel(participants, expectedPieces, countedPieces);
  const value = (n: number | null | undefined) =>
    typeof n === 'number' && Number.isSafeInteger(n) && n >= 0 ? n.toLocaleString() : '—';
  return (
    <details className="border border-base-300 p-3 print:hidden" data-testid="chip-pieces-review">
      <summary className="flex min-h-11 cursor-pointer items-center font-medium">
        {t('chipPieces.title')}
      </summary>
      <p className="my-2 text-sm">{t('chipPieces.savedOnly')}</p>
      {model.denominations.length === 0 ? (
        <p>{t('chipPieces.noDenominations')}</p>
      ) : (
        <div
          className="overflow-x-auto"
          tabIndex={0}
          role="region"
          aria-label={t('chipPieces.title')}
        >
          <table className="w-full whitespace-nowrap text-sm [&_td]:px-2 [&_td]:py-2 [&_th]:px-2 [&_th]:py-2">
            <thead>
              <tr>
                <th>{t('chipPieces.member')}</th>
                <th>{t('chipPieces.seat')}</th>
                {model.denominations.map((k) => (
                  <th key={k} className="text-right">
                    {Number(k).toLocaleString()}
                  </th>
                ))}
                <th className="text-right">{t('chipPieces.totalPieces')}</th>
                <th className="text-right">{t('chipPieces.totalChips')}</th>
                <th>{t('chipPieces.status')}</th>
              </tr>
            </thead>
            <tbody>
              {model.rows.map(({ row, hasCounts, total, chips, inconsistent, trusted }, i) => (
                <tr key={`${row.memberId ?? row.memberNo}:${i}`}>
                  <td>
                    {row.displayName || row.nickname || row.memberNo} ({row.memberNo})
                  </td>
                  <td>
                    {row.tableNo != null
                      ? t('settlementPage.result.seat', {
                          table: row.tableNo,
                          seat: row.seatNo ?? '—',
                        })
                      : (row.seat ?? '—')}
                  </td>
                  {model.denominations.map((k) => (
                    <td key={k} className="text-right">
                      {value(row.chipCounts?.[k])}
                    </td>
                  ))}
                  <td className="text-right">{value(total)}</td>
                  <td className="text-right">{value(chips)}</td>
                  <td>
                    {!hasCounts
                      ? t(
                          row.rankEligibility === 'PENDING_COUNT'
                            ? 'chipPieces.pending'
                            : 'chipPieces.noCount',
                        )
                      : inconsistent
                        ? t('chipPieces.inconsistent')
                        : !trusted
                          ? t('chipPieces.untrusted')
                          : total === null
                            ? t('chipPieces.incomplete')
                            : !row.countRevisionId
                              ? t('chipPieces.legacy')
                              : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-sm">
        {t('chipPieces.missing', { count: model.rows.filter((r) => r.total === null).length })}
      </p>
    </details>
  );
}
